"use client";

import { useEffect } from "react";
import Link from "next/link";
import Image from "next/image";
import { applyCSSVariablesToRoot, generateCSSVariables } from "@/lib/theme-utils";

interface SimplifiedLandingProps {
  tenant: {
    name: string;
    slug: string;
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    accentColor?: string;
    backgroundColor?: string;
    textColor?: string;
    footerBackgroundColor?: string;
    footerTransparentOnHomepage?: boolean;
    headerSticky?: boolean;
    headerTransparentInitial?: boolean;
  };
}

/**
 * Simplified landing page for whitelabel mode when no custom HTML is configured.
 * Shows tenant branding and a link to their public page.
 */
export function SimplifiedLanding({ tenant }: SimplifiedLandingProps) {
  // Apply tenant colors as CSS variables on mount
  useEffect(() => {
    const cssVariables = generateCSSVariables(tenant);
    applyCSSVariablesToRoot(cssVariables);
  }, [tenant]);

  // When header is sticky AND transparent initial is enabled, use negative margin
  // to allow content to extend under the transparent header on the homepage
  const shouldExtendUnderHeader = tenant.headerSticky && tenant.headerTransparentInitial;
  const heroMarginClass = shouldExtendUnderHeader ? '-mt-16 pt-16' : '';

  // When footerTransparentOnHomepage is enabled, make the background transparent
  // so the LayoutWrapper's background image shows through behind the footer
  const useTransparentBackground = tenant.footerTransparentOnHomepage;

  return (
    <div
      className={`flex-1 flex items-center justify-center ${heroMarginClass}`}
      style={{
        backgroundColor: useTransparentBackground ? 'transparent' : 'var(--theme-bg, #FFFFFF)',
        minHeight: useTransparentBackground ? undefined : '100vh',
      }}
    >
      <div className="text-center px-4">
        {tenant.logoUrl && (
          <div className="relative h-24 w-64 mx-auto mb-8">
            <Image
              src={tenant.logoUrl}
              alt={tenant.name}
              fill
              className="object-contain"
              unoptimized
            />
          </div>
        )}
        <h1
          className="text-5xl font-bold mb-6"
          style={{ color: 'var(--theme-text, #111827)' }}
        >
          {tenant.name}
        </h1>
        <p
          className="text-xl mb-8 max-w-2xl mx-auto"
          style={{ color: 'var(--theme-text, #6B7280)', opacity: 0.8 }}
        >
          Discover interactive scavenger hunts and earn digital rewards
        </p>
        <Link
          href={`/${tenant.slug}`}
          className="inline-block px-8 py-3 rounded-lg font-semibold text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: 'var(--theme-primary, #3B82F6)' }}
        >
          View Hunts
        </Link>
      </div>
    </div>
  );
}
